import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowSquareOut,
  CaretRight,
  DownloadSimple,
  MagnifyingGlass,
  PaperPlaneTilt,
  PencilSimple,
  Plus,
  Receipt,
  Trash,
  Users,
  UsersThree,
  X,
} from '@phosphor-icons/react';
import {
  createInvoiceBatch,
  deleteDraftInvoice,
  downloadInvoicePDF,
  getInvoice,
  getStripeAccount,
  issueInvoice,
  listInvoices,
  listInvoiceBatches,
  listInvoiceTypes,
  previewFamilyInvoice,
  createFamilyInvoice,
  recordManualPayment,
  updateInvoiceLines,
  updateInvoiceRecipient,
  voidInvoice,
  type BatchRecipientInput,
  type BatchSummary,
  type Invoice,
  type InvoiceLine,
  type ManualPaymentMethod,
  type RecordManualPaymentInput,
  type CreateFamilyInvoiceInput,
} from '@/api/billing';
import { listMembers, listDependents, type Member, type Dependent } from '@/api/members';
import { listTeams, listTeamMembers, type Team } from '@/api/teams';
import { listTeamCategories } from '@/api/teamCategories';
import { cn } from '@/lib/utils';
import { estimatePayout } from '@/lib/invoiceFees';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { SearchInput } from '@/components/ui/search-input';
import { Badge } from '@/components/ui/badge';
import { InvoiceSummaryPanel } from '@/components/billing/InvoiceSummaryPanel';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { RowActions, type RowAction } from '@/components/ui/row-actions';
import {
  TablePagination,
  useTablePaginationState,
} from '@/components/ui/table-pagination';
import {
  SortableHeaderButton,
  useTableSortState,
  type TableSortState,
} from '@/components/ui/table-sort';
import { useConfirm } from '@/components/ConfirmDialog';
import { useToast } from '@/components/Toast';
import { Checkbox } from '@/components/ui/checkbox';
import { DateInputISO } from '@/components/ui/date-input-iso';

// Required-field marker. Hoisted out of JSX so the jsx-no-literals lint
// rule doesn't flag a single asterisk that's a typographic convention,
// not user copy.
const REQUIRED_MARK = '*';

// Sort column allowlist for each list mode. Matches the backend's switch
// on opts.Sort — keep these arrays in sync with internal/services/billing/
// invoice.go's List/ListBatches.
const FLAT_SORT_COLUMNS = ['status', 'number', 'customer', 'issued', 'total'] as const;
type FlatSortColumn = (typeof FLAT_SORT_COLUMNS)[number];

const BATCH_SORT_COLUMNS = ['status', 'count', 'issued', 'total', 'created'] as const;
type BatchSortColumn = (typeof BATCH_SORT_COLUMNS)[number];

// Compact invoice list + create flow used as the third tab inside the
// Billing settings page. Two list modes:
//   - Batch (default): one row per batch_id. Click to drill into the
//     constituent invoices. Same idea as the team-members panel inside
//     a team row.
//   - Flat: every invoice on its own row — used to search for one member
//     and see all their invoices historically.
// One create entry point ("+ Skapa faktura") handles both single and
// many-recipient invoices via the unified mixed picker.

interface Props {
  slug: string;
  token: string | null;
  // currency locked org-wide on first issue (D-16). When unlocked we let
  // the admin pick at issue time; once locked we just display the existing
  // one.
  lockedCurrency: string;
  defaultCurrency: 'SEK' | 'NOK' | 'DKK' | 'EUR';
}

type ListMode = 'batch' | 'flat';

export function BillingInvoices({ slug, token, lockedCurrency, defaultCurrency }: Props) {
  const { t } = useTranslation('common');
  const qc = useQueryClient();
  const confirm = useConfirm();
  const toast = useToast();

  const [listMode, setListMode] = useState<ListMode>('batch');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [activeID, setActiveID] = useState<string | null>(null);
  const [drillBatchID, setDrillBatchID] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [familyOpen, setFamilyOpen] = useState(false);

  // Debounce the search box so we don't hit the server on every keystroke.
  // 250 ms matches the rest of the codebase (members search, recipient
  // picker, …) — enough to coalesce typing without feeling laggy.
  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(h);
  }, [search]);

  // Pagination + sort state — separate prefixes per list mode so flipping
  // the toggle doesn't land you on page 7 of a list that doesn't have 7
  // pages, or apply an order column that the other mode doesn't support.
  const flatPagination = useTablePaginationState({ prefix: 'inv', defaultSize: 25 });
  const batchPagination = useTablePaginationState({ prefix: 'batch', defaultSize: 25 });
  const flatSort = useTableSortState<FlatSortColumn>({
    columns: FLAT_SORT_COLUMNS,
    prefix: 'inv',
  });
  const batchSort = useTableSortState<BatchSortColumn>({
    columns: BATCH_SORT_COLUMNS,
    prefix: 'batch',
  });

  // Flat-list query. Active in flat mode, AND in batch mode while the
  // user is drilled into a specific batch — both paths render the same
  // invoice rows, just with different filter scope.
  const flatActive = listMode === 'flat' || drillBatchID !== null;
  const flatQuery = useQuery({
    queryKey: [
      'billing-invoices',
      slug,
      {
        status: statusFilter,
        search: debouncedSearch,
        batch_id: drillBatchID,
        sort: flatSort.sort,
        order: flatSort.dir,
        offset: flatPagination.offset,
        limit: flatPagination.limit,
      },
    ],
    queryFn: () =>
      listInvoices(token, slug, {
        limit: flatPagination.limit,
        offset: flatPagination.offset,
        ...(statusFilter && { status: statusFilter }),
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(drillBatchID && { batch_id: drillBatchID }),
        ...(flatSort.sort && { sort: flatSort.sort, order: flatSort.dir }),
      }),
    enabled: !!token && flatActive,
  });

  const batchQuery = useQuery({
    queryKey: [
      'billing-invoice-batches',
      slug,
      {
        status: statusFilter,
        search: debouncedSearch,
        sort: batchSort.sort,
        order: batchSort.dir,
        offset: batchPagination.offset,
        limit: batchPagination.limit,
      },
    ],
    queryFn: () =>
      listInvoiceBatches(token, slug, {
        limit: batchPagination.limit,
        offset: batchPagination.offset,
        ...(statusFilter && { status: statusFilter }),
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(batchSort.sort && { sort: batchSort.sort, order: batchSort.dir }),
      }),
    enabled: !!token && listMode === 'batch' && drillBatchID === null,
  });

  function invalidateAll() {
    void qc.invalidateQueries({ queryKey: ['billing-invoices', slug] });
    void qc.invalidateQueries({ queryKey: ['billing-invoice-batches', slug] });
  }

  // List-level delete (only for drafts). Confirmed via the design-system
  // dialog — same destructive treatment the editor's Delete button uses.
  const deleteRowMutation = useMutation({
    mutationFn: (invoiceID: string) => deleteDraftInvoice(token, slug, invoiceID),
    onSuccess: invalidateAll,
  });

  const downloadRowMutation = useMutation({
    mutationFn: (invoiceID: string) => downloadInvoicePDF(token, slug, invoiceID),
    onSuccess: ({ blob, filename }) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    onError: (err) => {
      const code = err instanceof Error ? err.message : 'unknown';
      toast.error(
        t(`billing.invoices.pdf.error.${code}`, {
          defaultValue: t('billing.invoices.pdf.error.unknown', {
            defaultValue: 'Could not download the PDF.',
          }) ?? '',
        }),
      );
    },
  });

  async function onDeleteRow(invoiceID: string, invoiceLabel: string) {
    const ok = await confirm({
      title: t('billing.invoices.deleteConfirmTitle', { defaultValue: 'Delete draft?' }),
      body: t('billing.invoices.deleteConfirmRowBody', {
        defaultValue: '{{label}} will be removed. This cannot be undone.',
        label: invoiceLabel,
      }),
      confirmLabel: t('billing.invoices.deleteCta', { defaultValue: 'Delete' }),
      tone: 'destructive',
    });
    if (!ok) return;
    deleteRowMutation.mutate(invoiceID);
  }

  const currencyForDisplay = lockedCurrency || defaultCurrency;
  const showingBatchList = listMode === 'batch' && drillBatchID === null;

  return (
    <div className="flex flex-col gap-4">
      {drillBatchID !== null && (
        <DrillInBreadcrumb
          onBack={() => setDrillBatchID(null)}
          batchID={drillBatchID}
          t={t}
        />
      )}

      <Toolbar
        listMode={listMode}
        onListModeChange={(m) => {
          setListMode(m);
          setDrillBatchID(null);
        }}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        search={search}
        onSearchChange={setSearch}
        onCreate={() => setCreateOpen(true)}
        onCreateFamily={() => setFamilyOpen(true)}
        t={t}
      />

      <InvoiceSummaryPanel slug={slug} />

      {showingBatchList ? (
        <BatchListSection
          loading={batchQuery.isPending}
          batches={batchQuery.data?.batches ?? []}
          total={batchQuery.data?.total ?? 0}
          page={batchPagination.page}
          size={batchPagination.size}
          onPageChange={batchPagination.setPage}
          onSizeChange={batchPagination.setSize}
          sortState={batchSort}
          onOpenBatch={(id) => setDrillBatchID(id)}
          onCreate={() => setCreateOpen(true)}
          currencyForDisplay={currencyForDisplay}
          t={t}
        />
      ) : (
        <FlatListSection
          loading={flatQuery.isPending}
          invoices={flatQuery.data?.invoices ?? []}
          total={flatQuery.data?.total ?? 0}
          page={flatPagination.page}
          size={flatPagination.size}
          onPageChange={flatPagination.setPage}
          onSizeChange={flatPagination.setSize}
          sortState={flatSort}
          onOpenInvoice={(id) => setActiveID(id)}
          onDownload={(id) => downloadRowMutation.mutate(id)}
          onDelete={onDeleteRow}
          onCreate={() => setCreateOpen(true)}
          downloadingID={
            downloadRowMutation.isPending ? downloadRowMutation.variables ?? null : null
          }
          currencyForDisplay={currencyForDisplay}
          t={t}
        />
      )}

      {activeID && (
        <InvoiceEditor
          slug={slug}
          token={token}
          invoiceID={activeID}
          lockedCurrency={lockedCurrency}
          defaultCurrency={defaultCurrency}
          onClose={() => {
            setActiveID(null);
            invalidateAll();
          }}
        />
      )}

      <CreateInvoicePanel
        open={createOpen}
        slug={slug}
        token={token}
        lockedCurrency={lockedCurrency}
        defaultCurrency={defaultCurrency}
        onClose={(opened) => {
          setCreateOpen(false);
          invalidateAll();
          // When the user finished by hitting "Save as draft" we open the
          // editor on the first invoice in the new batch so they can
          // continue tweaking — issued invoices skip this (they're locked).
          if (opened) setActiveID(opened);
        }}
      />

      {familyOpen && (
        <CreateFamilyInvoicePanel
          slug={slug}
          token={token}
          currency={lockedCurrency || defaultCurrency}
          onClose={(createdID) => {
            setFamilyOpen(false);
            invalidateAll();
            if (createdID) setActiveID(createdID);
          }}
        />
      )}
    </div>
  );
}

// Toolbar owns the list-mode segmented control, the search input, the
// status select, and the "Skapa faktura" button. It's purely presentational
// — state lives on the parent so the URL-backed pagination/sort can
// invalidate the right queries when something flips.
function Toolbar({
  listMode,
  onListModeChange,
  statusFilter,
  onStatusFilterChange,
  search,
  onSearchChange,
  onCreate,
  onCreateFamily,
  t,
}: {
  listMode: ListMode;
  onListModeChange: (m: ListMode) => void;
  statusFilter: string;
  onStatusFilterChange: (s: string) => void;
  search: string;
  onSearchChange: (s: string) => void;
  onCreate: () => void;
  onCreateFamily: () => void;
  t: TFunction<'common'>;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-pill border border-border bg-muted/40 p-1 text-sm">
          <ModeButton
            active={listMode === 'batch'}
            onClick={() => onListModeChange('batch')}
            icon={<UsersThree weight="bold" className="h-4 w-4" />}
            label={t('billing.invoices.mode.batches', { defaultValue: 'Batches' })}
          />
          <ModeButton
            active={listMode === 'flat'}
            onClick={() => onListModeChange('flat')}
            icon={<Users weight="bold" className="h-4 w-4" />}
            label={t('billing.invoices.mode.all', { defaultValue: 'All invoices' })}
          />
        </div>

        <SearchInput
          wrapperClassName="w-full sm:w-72"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={
            t('billing.invoices.searchPlaceholder', {
              defaultValue: 'Search by customer, email or number…',
            }) ?? ''
          }
          clearLabel={t('common.clearSearch')}
        />

        {/* Radix Select rejects an empty string as an option value, so
            "all statuses" maps to the sentinel "__all" both directions. */}
        <Select
          value={statusFilter === '' ? '__all' : statusFilter}
          onValueChange={(v) => onStatusFilterChange(v === '__all' ? '' : v)}
        >
          <SelectTrigger className="w-auto min-w-[10rem]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">
              {t('billing.invoices.statusAll', { defaultValue: 'All statuses' })}
            </SelectItem>
            {listMode === 'batch' && (
              <SelectItem value="mixed">
                {t('billing.invoices.status.mixed', { defaultValue: 'Mixed' })}
              </SelectItem>
            )}
            {(['draft', 'issued', 'partially_paid', 'paid', 'overdue', 'void'] as const).map(
              (s) => (
                <SelectItem key={s} value={s}>
                  {t(`billing.invoices.status.${s}`, { defaultValue: s })}
                </SelectItem>
              ),
            )}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2">
        <Button variant="outline" onClick={onCreateFamily}>
          <UsersThree weight="bold" className="h-4 w-4" />
          {t('billing.invoices.createFamilyCta', { defaultValue: 'Family invoice' })}
        </Button>
        <Button onClick={onCreate}>
          <Plus weight="bold" className="h-4 w-4" />
          {t('billing.invoices.createCta', { defaultValue: 'Create invoice' })}
        </Button>
      </div>
    </div>
  );
}

function ModeButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill px-3 py-1.5 font-semibold transition-colors',
        active
          ? 'bg-background text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function DrillInBreadcrumb({
  onBack,
  t,
}: {
  onBack: () => void;
  batchID: string;
  t: TFunction<'common'>;
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <button
        type="button"
        onClick={onBack}
        className="rounded-pill border border-border bg-background px-3 py-1 font-semibold text-muted-foreground transition-colors hover:text-foreground"
      >
        {t('billing.invoices.drillIn.back', { defaultValue: '← Back to batches' })}
      </button>
      <span className="text-muted-foreground">
        {t('billing.invoices.drillIn.showingGroup', {
          defaultValue: 'Showing invoices in this group',
        })}
      </span>
    </div>
  );
}

function InvoicesEmpty({
  onCreate,
  t,
}: {
  onCreate: () => void;
  t: TFunction<'common'>;
}) {
  return (
    <EmptyState
      icon={<Receipt weight="duotone" />}
      title={t('billing.invoices.emptyTitle', { defaultValue: 'No invoices yet' })}
      body={t('billing.invoices.emptyBody', {
        defaultValue:
          'Create your first invoice draft to start charging members. Drafts can be edited until you issue them.',
      })}
      actions={[
        {
          label: t('billing.invoices.createCta', { defaultValue: 'Create invoice' }),
          onClick: onCreate,
          icon: <Plus weight="bold" />,
        },
      ]}
    />
  );
}

function BatchListSection({
  loading,
  batches,
  total,
  page,
  size,
  onPageChange,
  onSizeChange,
  sortState,
  onOpenBatch,
  onCreate,
  currencyForDisplay,
  t,
}: {
  loading: boolean;
  batches: BatchSummary[];
  total: number;
  page: number;
  size: number;
  onPageChange: (p: number) => void;
  onSizeChange: (s: number) => void;
  sortState: TableSortState<BatchSortColumn>;
  onOpenBatch: (id: string) => void;
  onCreate: () => void;
  currencyForDisplay: string;
  t: TFunction<'common'>;
}) {
  if (loading) {
    return <TableSkeleton columns={6} />;
  }
  if (batches.length === 0) {
    return <InvoicesEmpty onCreate={onCreate} t={t} />;
  }
  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-hidden rounded-2xl border border-border bg-background">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[7rem]">
                <SortableHeaderButton
                  column="status"
                  label={t('billing.invoices.col.status', { defaultValue: 'Status' })}
                  state={sortState}
                />
              </TableHead>
              <TableHead>
                {t('billing.invoices.col.numberRange', { defaultValue: 'Number(s)' })}
              </TableHead>
              <TableHead>
                <SortableHeaderButton
                  column="count"
                  label={t('billing.invoices.col.recipients', { defaultValue: 'Recipients' })}
                  state={sortState}
                />
              </TableHead>
              <TableHead>
                <SortableHeaderButton
                  column="issued"
                  label={t('billing.invoices.col.issued', { defaultValue: 'Issued' })}
                  state={sortState}
                />
              </TableHead>
              <TableHead className="text-right">
                <SortableHeaderButton
                  column="total"
                  label={t('billing.invoices.col.total', { defaultValue: 'Total' })}
                  state={sortState}
                />
              </TableHead>
              <TableHead className="w-[44px] text-right">
                <span className="sr-only">{t('billing.invoices.col.expand', { defaultValue: 'Expand' })}</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {batches.map((b) => (
              <BatchRow
                key={b.batch_id}
                batch={b}
                currencyForDisplay={b.currency || currencyForDisplay}
                onOpen={() => onOpenBatch(b.batch_id)}
                t={t}
              />
            ))}
          </TableBody>
        </Table>
      </div>
      <TablePagination
        total={total}
        page={page}
        size={size}
        onPageChange={onPageChange}
        onSizeChange={onSizeChange}
      />
    </div>
  );
}

// Batch row. Renders the number range (e.g. "2026-0042 — 2026-0064" or
// just "—" while all members are still drafts), the recipient count, the
// issue date, and the summed total. Clicking anywhere on the row drills
// in to the flat-list view filtered to that batch.
function BatchRow({
  batch,
  currencyForDisplay,
  onOpen,
  t,
}: {
  batch: BatchSummary;
  currencyForDisplay: string;
  onOpen: () => void;
  t: TFunction<'common'>;
}) {
  const numberLabel =
    batch.number_first && batch.number_last
      ? batch.number_first === batch.number_last
        ? batch.number_first
        : `${batch.number_first} — ${batch.number_last}`
      : t('billing.invoices.draftPlaceholder', { defaultValue: '— draft —' });
  const issueDate = batch.issue_date
    ? new Date(batch.issue_date).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '—';
  return (
    <TableRow className="cursor-pointer" onClick={onOpen}>
      <TableCell>
        <Badge variant={batch.summary_status === 'paid' ? 'default' : 'secondary'}>
          {t(`billing.invoices.status.${batch.summary_status}`, {
            defaultValue: batch.summary_status,
          })}
        </Badge>
      </TableCell>
      <TableCell className="font-mono text-sm text-muted-foreground">{numberLabel}</TableCell>
      <TableCell className="text-sm">
        {t('billing.invoices.recipientsCount', {
          defaultValue: '{{n}} recipients',
          n: batch.invoice_count,
        })}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">{issueDate}</TableCell>
      <TableCell className="text-right text-sm font-medium tabular-nums">
        {formatMoney(batch.total_minor, currencyForDisplay)}
      </TableCell>
      <TableCell className="w-[44px] text-right text-muted-foreground">
        <CaretRight weight="bold" className="ml-auto h-4 w-4" aria-hidden />
      </TableCell>
    </TableRow>
  );
}

function FlatListSection({
  loading,
  invoices,
  total,
  page,
  size,
  onPageChange,
  onSizeChange,
  sortState,
  onOpenInvoice,
  onDownload,
  onDelete,
  onCreate,
  downloadingID,
  currencyForDisplay,
  t,
}: {
  loading: boolean;
  invoices: Invoice[];
  total: number;
  page: number;
  size: number;
  onPageChange: (p: number) => void;
  onSizeChange: (s: number) => void;
  sortState: TableSortState<FlatSortColumn>;
  onOpenInvoice: (id: string) => void;
  onDownload: (id: string) => void;
  onDelete: (id: string, label: string) => void;
  onCreate: () => void;
  downloadingID: string | null;
  currencyForDisplay: string;
  t: TFunction<'common'>;
}) {
  if (loading) {
    return <TableSkeleton columns={6} />;
  }
  if (invoices.length === 0) {
    return <InvoicesEmpty onCreate={onCreate} t={t} />;
  }
  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-hidden rounded-2xl border border-border bg-background">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[7rem]">
                <SortableHeaderButton
                  column="status"
                  label={t('billing.invoices.col.status', { defaultValue: 'Status' })}
                  state={sortState}
                />
              </TableHead>
              <TableHead>
                <SortableHeaderButton
                  column="number"
                  label={t('billing.invoices.col.number', { defaultValue: 'Number' })}
                  state={sortState}
                />
              </TableHead>
              <TableHead>
                <SortableHeaderButton
                  column="customer"
                  label={t('billing.invoices.col.customer', { defaultValue: 'Customer' })}
                  state={sortState}
                />
              </TableHead>
              <TableHead>
                <SortableHeaderButton
                  column="issued"
                  label={t('billing.invoices.col.issued', { defaultValue: 'Issued' })}
                  state={sortState}
                />
              </TableHead>
              <TableHead className="text-right">
                <SortableHeaderButton
                  column="total"
                  label={t('billing.invoices.col.total', { defaultValue: 'Total' })}
                  state={sortState}
                />
              </TableHead>
              <TableHead className="w-[64px] text-right">
                <span className="sr-only">
                  {t('rowActions.columnHeader', { defaultValue: 'Actions' })}
                </span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((inv) => (
              <InvoiceRow
                key={inv.id}
                inv={inv}
                currencyForDisplay={inv.currency || currencyForDisplay}
                onOpen={() => onOpenInvoice(inv.id)}
                onDownload={() => onDownload(inv.id)}
                onDelete={(label) => onDelete(inv.id, label)}
                downloading={downloadingID === inv.id}
                t={t}
              />
            ))}
          </TableBody>
        </Table>
      </div>
      <TablePagination
        total={total}
        page={page}
        size={size}
        onPageChange={onPageChange}
        onSizeChange={onSizeChange}
      />
    </div>
  );
}

// InvoiceRow renders one invoice as a row in the flat list. The actions
// cell hosts the 3-dot menu with status-aware entries:
//   - drafts: Edit, Delete
//   - issued / paid / partially_paid / overdue: Download PDF
//   - void: read-only (no actions)
function InvoiceRow({
  inv,
  currencyForDisplay,
  onOpen,
  onDownload,
  onDelete,
  downloading,
  t,
}: {
  inv: Invoice;
  currencyForDisplay: string;
  onOpen: () => void;
  onDownload: () => void;
  onDelete: (label: string) => void;
  downloading: boolean;
  t: TFunction<'common'>;
}) {
  const isDraft = inv.status === 'draft';
  const isVoid = inv.status === 'void';
  const label = inv.number || t('billing.invoices.draftPlaceholder', { defaultValue: '— draft —' });
  const issueDate = inv.issue_date
    ? new Date(inv.issue_date).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '—';

  const menuActions: RowAction[] = [];
  if (isDraft) {
    menuActions.push({
      id: 'edit',
      label: t('billing.invoices.actions.edit', { defaultValue: 'Edit' }),
      icon: PencilSimple,
      onSelect: onOpen,
    });
  } else {
    menuActions.push({
      id: 'open',
      label: t('billing.invoices.actions.open', { defaultValue: 'Open invoice details' }),
      icon: ArrowSquareOut,
      onSelect: onOpen,
    });
  }
  if (!isDraft && !isVoid) {
    menuActions.push({
      id: 'download',
      label: downloading
        ? t('billing.invoices.pdf.downloading', { defaultValue: 'Downloading…' })
        : t('billing.invoices.pdf.download', { defaultValue: 'Download PDF' }),
      icon: DownloadSimple,
      onSelect: onDownload,
      disabled: downloading,
    });
  }
  if (isDraft) {
    menuActions.push({
      id: 'delete',
      label: t('billing.invoices.actions.delete', { defaultValue: 'Delete' }),
      icon: Trash,
      destructive: true,
      onSelect: () => onDelete(label),
    });
  }

  return (
    <TableRow>
      <TableCell>
        <Badge variant={inv.status === 'paid' ? 'default' : 'secondary'}>
          {t(`billing.invoices.status.${inv.status}`, { defaultValue: inv.status })}
        </Badge>
      </TableCell>
      <TableCell className="font-mono text-sm text-muted-foreground">{label}</TableCell>
      <TableCell className="truncate text-sm">{inv.customer_name}</TableCell>
      <TableCell className="text-sm text-muted-foreground">{issueDate}</TableCell>
      <TableCell className="text-right text-sm font-medium tabular-nums">
        {formatMoney(inv.total_minor, currencyForDisplay)}
      </TableCell>
      <TableCell className="w-[64px] text-right">
        {menuActions.length > 0 && (
          <div data-row-actions>
            <RowActions menuActions={menuActions} />
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}

// useClickOutside wires a click-outside-to-close handler to a ref. The
// side panels use it so clicking the dimmed body of the page dismisses
// them — matches the affordance of a modal backdrop without the modality.
// Skips when the panel is closed (no ref attached) and ignores clicks
// that originated inside the panel itself.
function useClickOutside(
  ref: { current: HTMLElement | null },
  active: boolean,
  onOutside: () => void,
) {
  useEffect(() => {
    if (!active) return undefined;
    function handle(e: MouseEvent) {
      const el = ref.current;
      if (!el) return;
      const target = e.target as Node | null;
      if (!target) return;
      if (el.contains(target)) return;
      // While a Radix Select/Dropdown is open it locks <body> to
      // pointer-events:none so a single outside click only dismisses the
      // menu. That same click then resolves its target to <body>/<html>
      // (the nearest element still accepting pointer events), which sits
      // outside the panel and would otherwise close the whole panel. Treat
      // that case as the menu dismissing itself, not a panel-closing click.
      if (
        document.body.style.pointerEvents === 'none' ||
        target === document.body ||
        target === document.documentElement
      )
        return;
      // Radix portals (Select dropdowns, etc.) render outside the panel's DOM
      // subtree but are logically owned by it — don't dismiss on those clicks.
      // Popper-positioned content (Select/Dropdown/Popover/Menu) uses
      // data-radix-popper-content-wrapper; portal-only content keeps the older
      // data-radix-portal marker.
      if (
        (target as Element).closest?.(
          '[data-radix-popper-content-wrapper],[data-radix-portal]',
        )
      )
        return;
      onOutside();
    }
    // mousedown so the dismiss fires before any focus/onClick on the new
    // target steals focus — feels snappier than `click`.
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [ref, active, onOutside]);
}

// ----------------- Editor (single existing invoice) ------------------

interface EditorProps {
  slug: string;
  token: string | null;
  invoiceID: string;
  lockedCurrency: string;
  defaultCurrency: 'SEK' | 'NOK' | 'DKK' | 'EUR';
  onClose: () => void;
}

function InvoiceEditor({
  slug,
  token,
  invoiceID,
  lockedCurrency,
  defaultCurrency,
  onClose,
}: EditorProps) {
  const { t } = useTranslation('common');
  const qc = useQueryClient();
  const confirm = useConfirm();
  const panelRef = useRef<HTMLDivElement>(null);
  useClickOutside(panelRef, true, onClose);

  const invQuery = useQuery({
    queryKey: ['billing-invoice', slug, invoiceID],
    queryFn: () => getInvoice(token, slug, invoiceID),
    enabled: !!token,
  });
  const inv = invQuery.data;

  const [lines, setLines] = useState<InvoiceLine[]>([]);
  useMemo(() => {
    if (inv?.lines) setLines(inv.lines);
    return null;
    // Re-seed editable line state only when the invoice identity changes;
    // depending on inv.lines would clobber in-progress edits on every refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inv?.id]);

  const [recipient, setRecipient] = useState<RecipientForm>(emptyRecipient());
  useMemo(() => {
    if (inv) setRecipient(toRecipientForm(inv));
    return null;
    // Re-seed the recipient form only when the invoice identity changes;
    // depending on inv would clobber in-progress edits on every refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inv?.id]);

  const saveRecipientMutation = useMutation({
    mutationFn: () =>
      updateInvoiceRecipient(token, slug, invoiceID, {
        ...(recipient.member_id && { member_id: recipient.member_id }),
        invoice_type_id: recipient.invoice_type_id,
        customer_name: recipient.customer_name,
        ...(recipient.customer_email && { customer_email: recipient.customer_email }),
        ...(recipient.customer_address_line1 && {
          customer_address_line1: recipient.customer_address_line1,
        }),
        ...(recipient.customer_address_line2 && {
          customer_address_line2: recipient.customer_address_line2,
        }),
        ...(recipient.customer_postal_code && {
          customer_postal_code: recipient.customer_postal_code,
        }),
        ...(recipient.customer_city && { customer_city: recipient.customer_city }),
        ...(recipient.customer_country && { customer_country: recipient.customer_country }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['billing-invoice', slug, invoiceID] });
      void qc.invalidateQueries({ queryKey: ['billing-invoices', slug] });
      void qc.invalidateQueries({ queryKey: ['billing-invoice-batches', slug] });
    },
  });

  const saveMutation = useMutation({
    mutationFn: () => updateInvoiceLines(token, slug, invoiceID, lines),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['billing-invoice', slug, invoiceID] });
      void qc.invalidateQueries({ queryKey: ['billing-invoices', slug] });
      void qc.invalidateQueries({ queryKey: ['billing-invoice-batches', slug] });
    },
  });

  const issueMutation = useMutation({
    mutationFn: async () => {
      const total = lines.reduce((acc, l) => acc + lineTotal(l) + lineVAT(l), 0);
      const ok = await confirm({
        title: t('billing.invoices.issueConfirmTitle', { defaultValue: 'Issue this invoice?' }),
        body: t('billing.invoices.issueConfirmBody', {
          defaultValue:
            'A number and OCR will be allocated and the invoice becomes immutable. Total: {{total}}',
          total: formatMoney(total, lockedCurrency || defaultCurrency),
        }),
        confirmLabel: t('billing.invoices.issueCta', { defaultValue: 'Issue invoice' }),
        tone: 'default',
      });
      if (!ok) throw new Error('cancelled');
      const today = new Date().toISOString().slice(0, 10);
      const due = new Date();
      due.setDate(due.getDate() + 30);
      return issueInvoice(token, slug, invoiceID, {
        currency: lockedCurrency || defaultCurrency,
        issue_date: today,
        due_date: due.toISOString().slice(0, 10),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['billing-invoice', slug, invoiceID] });
      void qc.invalidateQueries({ queryKey: ['billing-invoices', slug] });
      void qc.invalidateQueries({ queryKey: ['billing-invoice-batches', slug] });
    },
  });

  const voidMutation = useMutation({
    mutationFn: async () => {
      const ok = await confirm({
        title: t('billing.invoices.voidConfirmTitle', { defaultValue: 'Void this invoice?' }),
        body: t('billing.invoices.voidConfirmBody', {
          defaultValue:
            'The invoice number is preserved for audit but it can no longer be paid.',
        }),
        confirmLabel: t('billing.invoices.voidCta', { defaultValue: 'Void' }),
        tone: 'destructive',
      });
      if (!ok) throw new Error('cancelled');
      return voidInvoice(token, slug, invoiceID, '');
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['billing-invoice', slug, invoiceID] });
      void qc.invalidateQueries({ queryKey: ['billing-invoices', slug] });
      void qc.invalidateQueries({ queryKey: ['billing-invoice-batches', slug] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const ok = await confirm({
        title: t('billing.invoices.deleteConfirmTitle', { defaultValue: 'Delete draft?' }),
        body: t('billing.invoices.deleteConfirmBody', { defaultValue: 'This cannot be undone.' }),
        confirmLabel: t('billing.invoices.deleteCta', { defaultValue: 'Delete' }),
        tone: 'destructive',
      });
      if (!ok) throw new Error('cancelled');
      return deleteDraftInvoice(token, slug, invoiceID);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['billing-invoices', slug] });
      void qc.invalidateQueries({ queryKey: ['billing-invoice-batches', slug] });
      onClose();
    },
  });

  if (!inv) return null;
  const isDraft = inv.status === 'draft';
  const subtotal = lines.reduce((acc, l) => acc + lineTotal(l), 0);
  const vat = lines.reduce((acc, l) => acc + lineVAT(l), 0);
  const total = subtotal + vat;
  const currencyForDisplay = inv.currency || lockedCurrency || defaultCurrency;

  return (
    <aside
      ref={panelRef}
      className="fixed inset-y-0 right-0 z-30 flex w-full max-w-2xl flex-col gap-4 overflow-y-auto border-l border-border/60 bg-background p-6 shadow-2xl"
    >
      <header className="flex items-center justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-semibold">
            {inv.number || t('billing.invoices.draftPlaceholder', { defaultValue: '— draft —' })}
            <Badge variant={inv.status === 'paid' ? 'default' : 'secondary'}>
              {t(`billing.invoices.status.${inv.status}`, { defaultValue: inv.status })}
            </Badge>
          </h3>
          <p className="text-sm text-muted-foreground">{inv.customer_name}</p>
        </div>
        <Button variant="ghost" onClick={onClose} aria-label={t('common.cancel')}>
          <X weight="bold" />
        </Button>
      </header>

      <RecipientSection
        slug={slug}
        token={token}
        inv={inv}
        recipient={recipient}
        setRecipient={setRecipient}
        onSave={() => saveRecipientMutation.mutate()}
        saving={saveRecipientMutation.isPending}
      />

      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold">
            {t('billing.invoices.lines', { defaultValue: 'Line items' })}
          </h4>
          {isDraft && (
            <Button
              variant="outline"
              onClick={() =>
                setLines([
                  ...lines,
                  {
                    ord: lines.length,
                    description: '',
                    quantity_milli: 1000,
                    unit_amount_minor: 0,
                    vat_rate_bp: 0,
                  },
                ])
              }
            >
              <Plus weight="bold" />
              {t('billing.invoices.addLine', { defaultValue: 'Add line' })}
            </Button>
          )}
        </div>
        {lines.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('billing.invoices.linesEmpty', { defaultValue: 'No lines yet.' })}
          </p>
        ) : (
          <LineEditor lines={lines} setLines={setLines} disabled={!isDraft} t={t} />
        )}
      </section>

      <dl className="grid grid-cols-2 gap-y-1 rounded-2xl bg-muted/40 p-4 text-sm">
        <dt>{t('billing.invoices.subtotal', { defaultValue: 'Subtotal' })}</dt>
        <dd className="text-right">{formatMoney(subtotal, currencyForDisplay)}</dd>
        <dt>{t('billing.invoices.vat', { defaultValue: 'VAT' })}</dt>
        <dd className="text-right">{formatMoney(vat, currencyForDisplay)}</dd>
        <dt className="font-semibold">{t('billing.invoices.total', { defaultValue: 'Total' })}</dt>
        <dd className="text-right font-semibold">{formatMoney(total, currencyForDisplay)}</dd>
      </dl>

      {(inv.status === 'issued' ||
        inv.status === 'partially_paid' ||
        inv.status === 'overdue') && (
        <ManualPaymentSection
          slug={slug}
          token={token}
          invoiceID={invoiceID}
          currency={currencyForDisplay}
          amountDueMinor={Math.max(0, total - inv.amount_paid_minor)}
          onRecorded={() => {
            void qc.invalidateQueries({ queryKey: ['billing-invoice', slug, invoiceID] });
            void qc.invalidateQueries({ queryKey: ['billing-invoices', slug] });
            void qc.invalidateQueries({ queryKey: ['billing-invoice-batches', slug] });
          }}
        />
      )}

      <footer className="mt-auto flex flex-wrap items-center justify-end gap-2">
        {isDraft && (
          <>
            <Button variant="outline" onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending}>
              {t('billing.invoices.deleteDraftCta', { defaultValue: 'Delete draft' })}
            </Button>
            <Button variant="outline" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              {t('billing.invoices.saveLines', { defaultValue: 'Save lines' })}
            </Button>
            <Button
              onClick={() => issueMutation.mutate()}
              disabled={issueMutation.isPending || lines.length === 0}
            >
              {t('billing.invoices.issueCta', { defaultValue: 'Issue invoice' })}
            </Button>
          </>
        )}
        {!isDraft && (
          <PDFActions slug={slug} token={token} invoiceID={invoiceID} />
        )}
        {!isDraft && inv.status !== 'void' && (
          <Button variant="destructive" onClick={() => voidMutation.mutate()} disabled={voidMutation.isPending}>
            {t('billing.invoices.voidCta', { defaultValue: 'Void' })}
          </Button>
        )}
      </footer>
    </aside>
  );
}

// ManualPaymentSection lets an admin record a payment that arrived outside
// Stripe — bankgiro / plusgiro / Swish / cash. Collapsed by default; expands to
// a small form prefilled with the amount due. Converges on the same backend
// status path Stripe uses (see billing.PaymentService.RecordManualPayment).
const MANUAL_METHODS: ManualPaymentMethod[] = ['bank_transfer', 'swish', 'cash', 'other'];

function ManualPaymentSection({
  slug,
  token,
  invoiceID,
  currency,
  amountDueMinor,
  onRecorded,
}: {
  slug: string;
  token: string | null;
  invoiceID: string;
  currency: string;
  amountDueMinor: number;
  onRecorded: () => void;
}) {
  const { t } = useTranslation('common');
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [amountMajor, setAmountMajor] = useState((amountDueMinor / 100).toFixed(2));
  const [method, setMethod] = useState<ManualPaymentMethod>('bank_transfer');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState('');

  const mutation = useMutation({
    mutationFn: () => {
      const input: RecordManualPaymentInput = {
        amount_minor: Math.round(Number.parseFloat(amountMajor) * 100),
        method,
      };
      if (date) input.received_date = date;
      if (reference) input.reference = reference;
      return recordManualPayment(token, slug, invoiceID, input);
    },
    onSuccess: () => {
      toast.success(t('billing.invoices.manualPayment.recorded', { defaultValue: 'Payment recorded.' }));
      setOpen(false);
      setReference('');
      onRecorded();
    },
    onError: () =>
      toast.error(t('billing.invoices.manualPayment.failed', { defaultValue: "Couldn't record the payment." })),
  });

  const amountValid = Number.parseFloat(amountMajor) > 0;

  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-border/40 p-4">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold">
          {t('billing.invoices.manualPayment.title', { defaultValue: 'Offline payment' })}
        </h4>
        {!open && (
          <Button variant="outline" onClick={() => setOpen(true)}>
            {t('billing.invoices.manualPayment.cta', { defaultValue: 'Record payment' })}
          </Button>
        )}
      </div>
      {!open ? (
        <p className="text-xs text-muted-foreground">
          {t('billing.invoices.manualPayment.hint', {
            defaultValue:
              'Mark this invoice paid by bank transfer, Swish or cash — for payments that did not go through the online card flow.',
          })}
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            <span>
              {t('billing.invoices.manualPayment.amountWithCurrency', {
                defaultValue: 'Amount ({{currency}})',
                currency,
              })}
            </span>
            <Input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={amountMajor}
              onChange={(e) => setAmountMajor(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            <span>{t('billing.invoices.manualPayment.method', { defaultValue: 'Method' })}</span>
            <Select value={method} onValueChange={(v) => setMethod(v as ManualPaymentMethod)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MANUAL_METHODS.map((m) => (
                  <SelectItem key={m} value={m}>
                    {t(`billing.invoices.manualPayment.methods.${m}`, { defaultValue: m })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            <span>{t('billing.invoices.manualPayment.date', { defaultValue: 'Received date' })}</span>
            <DateInputISO value={date} onChange={setDate} />
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            <span>{t('billing.invoices.manualPayment.reference', { defaultValue: 'Reference' })}</span>
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              maxLength={200}
              placeholder={t('billing.invoices.manualPayment.referencePlaceholder', {
                defaultValue: 'e.g. bank reference',
              })}
            />
          </label>
          <div className="flex items-center justify-end gap-2 sm:col-span-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={() => mutation.mutate()} disabled={mutation.isPending || !amountValid}>
              {t('billing.invoices.manualPayment.submit', { defaultValue: 'Record payment' })}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

// LineEditor is the shared per-line editor used by both the existing
// InvoiceEditor and the new CreateInvoicePanel. Disabled rows render with
// dimmed inputs (issued invoices show their lines but aren't editable).
function LineEditor({
  lines,
  setLines,
  disabled,
  t,
}: {
  lines: InvoiceLine[];
  setLines: (next: InvoiceLine[]) => void;
  disabled?: boolean;
  t: TFunction<'common'>;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div
        className="grid grid-cols-12 gap-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground"
        role="row"
        aria-hidden
      >
        <div className="col-span-5">
          {t('billing.invoices.col.description', { defaultValue: 'Description' })}
        </div>
        <div className="col-span-2">
          {t('billing.invoices.col.quantity', { defaultValue: 'Qty' })}
        </div>
        <div className="col-span-2">
          {t('billing.invoices.col.unitPrice', { defaultValue: 'Unit price' })}
        </div>
        <div className="col-span-2">
          {t('billing.invoices.col.vat', { defaultValue: 'VAT %' })}
        </div>
        <div className="col-span-1" />
      </div>
      <ul className="flex flex-col gap-2">
        {lines.map((l, idx) => (
          <li
            key={idx}
            className="grid grid-cols-12 items-center gap-2"
          >
            <div className="col-span-5">
              <Input
                placeholder={t('billing.invoices.col.description', { defaultValue: 'Description' }) ?? ''}
                aria-label={t('billing.invoices.col.description', { defaultValue: 'Description' }) ?? ''}
                value={l.description}
                disabled={disabled}
                onChange={(e) => updateLine(setLines, lines, idx, { description: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Input
                type="number"
                step="0.001"
                aria-label={t('billing.invoices.col.quantity', { defaultValue: 'Qty' }) ?? ''}
                value={l.quantity_milli / 1000}
                disabled={disabled}
                onChange={(e) =>
                  updateLine(setLines, lines, idx, {
                    quantity_milli: Math.round(Number(e.target.value) * 1000),
                  })
                }
              />
            </div>
            <div className="col-span-2">
              <Input
                type="number"
                step="0.01"
                aria-label={t('billing.invoices.col.unitPrice', { defaultValue: 'Unit price' }) ?? ''}
                value={l.unit_amount_minor / 100}
                disabled={disabled}
                onChange={(e) =>
                  updateLine(setLines, lines, idx, {
                    unit_amount_minor: Math.round(Number(e.target.value) * 100),
                  })
                }
              />
            </div>
            <div className="col-span-2">
              <Input
                type="number"
                step="0.5"
                aria-label={t('billing.invoices.col.vat', { defaultValue: 'VAT %' }) ?? ''}
                value={l.vat_rate_bp / 100}
                disabled={disabled}
                onChange={(e) =>
                  updateLine(setLines, lines, idx, {
                    vat_rate_bp: Math.round(Number(e.target.value) * 100),
                  })
                }
              />
            </div>
            <div className="col-span-1 flex items-center justify-end">
              {!disabled && (
                <Button
                  variant="ghost"
                  onClick={() => setLines(lines.filter((_, i) => i !== idx))}
                  aria-label={t('billing.invoices.removeLine', { defaultValue: 'Remove line' })}
                >
                  <Trash weight="bold" />
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// PDFActions handles the download / regenerate buttons for an issued
// invoice. The download triggers a Blob fetch (so the auth header is
// honoured) and saves via an in-memory anchor — no popup blocker issue.
function PDFActions({
  slug,
  token,
  invoiceID,
}: {
  slug: string;
  token: string | null;
  invoiceID: string;
}) {
  const { t } = useTranslation('common');
  const toast = useToast();
  const downloadMutation = useMutation({
    mutationFn: () => downloadInvoicePDF(token, slug, invoiceID),
    onSuccess: ({ blob, filename }) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    onError: (err) => {
      const code = err instanceof Error ? err.message : 'unknown';
      toast.error(
        t(`billing.invoices.pdf.error.${code}`, {
          defaultValue: t('billing.invoices.pdf.error.unknown', {
            defaultValue: 'Could not download the PDF.',
          }) ?? '',
        }),
      );
    },
  });
  return (
    <Button
      onClick={() => downloadMutation.mutate()}
      disabled={downloadMutation.isPending}
    >
      <DownloadSimple weight="bold" />
      {downloadMutation.isPending
        ? t('billing.invoices.pdf.downloading', { defaultValue: 'Downloading…' })
        : t('billing.invoices.pdf.download', { defaultValue: 'Download PDF' })}
    </Button>
  );
}

// ----------------- CreateInvoicePanel (the unified create flow) ------------------

// Recipient as held in the create form. Either a `member` (linked to
// members.member) or a `manual` row (free-typed customer_name). We keep
// the distinction so the chip list can render an avatar/role tag for
// members and a generic tag for manual entries, and so the "exclude
// leaders" toggle has something to act on.
type CreateRecipient =
  | {
      kind: 'member';
      member_id: string;
      first_name: string;
      last_name: string;
      email: string;
      role?: 'athlete' | 'leader';
      team_id?: string; // only set when added via the team picker
    }
  | {
      kind: 'manual';
      tempID: string;
      customer_name: string;
      customer_email: string;
    };

function CreateInvoicePanel({
  open,
  slug,
  token,
  lockedCurrency,
  defaultCurrency,
  onClose,
}: {
  open: boolean;
  slug: string;
  token: string | null;
  lockedCurrency: string;
  defaultCurrency: 'SEK' | 'NOK' | 'DKK' | 'EUR';
  onClose: (openedInvoiceID?: string) => void;
}) {
  const { t } = useTranslation('common');
  const confirm = useConfirm();
  const panelRef = useRef<HTMLDivElement>(null);
  useClickOutside(panelRef, open, () => onClose());

  const [recipients, setRecipients] = useState<CreateRecipient[]>([]);
  const [excludeLeaders, setExcludeLeaders] = useState(false);
  const [lines, setLines] = useState<InvoiceLine[]>([
    {
      ord: 0,
      description: '',
      quantity_milli: 1000,
      unit_amount_minor: 0,
      vat_rate_bp: 0,
    },
  ]);
  const [notes, setNotes] = useState('');
  const [errorCode, setErrorCode] = useState<string | null>(null);

  // Reset form whenever the panel opens — stale state from a previous
  // session shouldn't carry over.
  useEffect(() => {
    if (open) {
      setRecipients([]);
      setExcludeLeaders(false);
      setLines([
        {
          ord: 0,
          description: '',
          quantity_milli: 1000,
          unit_amount_minor: 0,
          vat_rate_bp: 0,
        },
      ]);
      setNotes('');
      setErrorCode(null);
    }
  }, [open]);

  const visibleRecipients = useMemo(
    () =>
      recipients.filter((r) => {
        if (!excludeLeaders) return true;
        return r.kind !== 'member' || r.role !== 'leader';
      }),
    [recipients, excludeLeaders],
  );

  const subtotal = lines.reduce((acc, l) => acc + lineTotal(l), 0);
  const vat = lines.reduce((acc, l) => acc + lineVAT(l), 0);
  const total = subtotal + vat;
  const currencyForDisplay = lockedCurrency || defaultCurrency;
  const linesValid =
    lines.length > 0 &&
    lines.every((l) => l.description.trim().length > 0 && l.unit_amount_minor >= 0);
  // Every visible recipient must have a usable customer_name. Members
  // always do (first_name+last_name); manual entries are valid only
  // when the user filled in the name field.
  const recipientsValid =
    visibleRecipients.length > 0 &&
    visibleRecipients.every((r) =>
      r.kind === 'member'
        ? `${r.first_name} ${r.last_name}`.trim().length > 0
        : r.customer_name.trim().length > 0,
    );
  const valid = recipientsValid && linesValid;

  // A DRAFT can be saved while still incomplete — its lines need no description
  // yet (only sending requires that). It still needs ≥1 recipient and ≥1 line
  // with a sane quantity/amount, matching the backend's lenient draft path.
  const linesPresent =
    lines.length > 0 && lines.every((l) => l.quantity_milli > 0 && l.unit_amount_minor >= 0);
  const draftValid = recipientsValid && linesPresent;

  // Stripe readiness gates SENDING (issue) — a sent invoice expects payment,
  // and members can't pay until Connect is charges-enabled. Drafting stays
  // open. Shares the cache key with the settings → billing Stripe panel.
  const stripeQuery = useQuery({
    queryKey: ['billing-stripe-account', slug],
    queryFn: () => getStripeAccount(token, slug),
    enabled: open && !!slug,
  });
  const stripeReady = !!(stripeQuery.data?.connected && stripeQuery.data?.charges_enabled);

  // submit — issueNow=true takes the issue-or-draft branch; the user
  // confirms first via the design-system dialog (CLAUDE.md mandates: no
  // window.confirm). Draft saves don't confirm — that's the safe default
  // path.
  const submitMutation = useMutation({
    mutationFn: async (issueNow: boolean) => {
      if (issueNow) {
        const ok = await confirm({
          title: t('billing.invoices.create.confirmIssueTitle', {
            defaultValue: 'Issue {{n}} invoice(s)?',
            n: visibleRecipients.length,
          }),
          body: t('billing.invoices.create.confirmIssueBody', {
            defaultValue:
              'Each recipient will receive a finalised invoice with its own number and OCR. Total per invoice: {{total}}.',
            total: formatMoney(total, currencyForDisplay),
          }),
          confirmLabel: t('billing.invoices.create.confirmIssueCta', {
            defaultValue: 'Issue {{n}} invoice(s)',
            n: visibleRecipients.length,
          }),
          tone: 'default',
        });
        if (!ok) throw new Error('cancelled');
      }
      const today = new Date().toISOString().slice(0, 10);
      const due = new Date();
      due.setDate(due.getDate() + 30);
      const payloadRecipients: BatchRecipientInput[] = visibleRecipients.map((r) =>
        r.kind === 'member'
          ? {
              member_id: r.member_id,
              customer_name: `${r.first_name} ${r.last_name}`.trim() || 'Member',
              ...(r.email && { customer_email: r.email }),
            }
          : {
              customer_name: r.customer_name.trim() || 'Recipient',
              ...(r.customer_email && { customer_email: r.customer_email }),
            },
      );
      return createInvoiceBatch(token, slug, {
        recipients: payloadRecipients,
        lines: lines.map((l, i) => ({ ...l, ord: i })),
        ...(notes && { notes }),
        issue_now: issueNow,
        ...(issueNow && {
          currency: lockedCurrency || defaultCurrency,
          issue_date: today,
          due_date: due.toISOString().slice(0, 10),
        }),
      });
    },
    onSuccess: (out, issueNow) => {
      // Open the first new invoice in the editor only on draft save — the
      // user just made it, they probably want to refine. Issue-now means
      // they were ready; let them see the new batch row instead.
      const firstID = out.invoice_ids[0];
      onClose(!issueNow && firstID ? firstID : undefined);
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'cancelled') return;
      setErrorCode(err instanceof Error ? err.message : 'unknown');
    },
  });

  // Mount the panel only when open so the queries inside the recipient
  // picker don't fire when the panel is closed.
  if (!open) return null;

  return (
    <aside
      ref={panelRef}
      className="fixed inset-y-0 right-0 z-40 flex w-full max-w-3xl flex-col gap-4 overflow-y-auto border-l border-border/60 bg-background p-6 shadow-2xl"
    >
      <header className="flex items-center justify-between">
        <div>
          <span className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t('billing.invoices.create.eyebrow', { defaultValue: 'Create invoice' })}
          </span>
          <h3 className="text-lg font-semibold">
            {t('billing.invoices.create.title', {
              defaultValue: 'New invoice ({{n}} recipient(s))',
              n: visibleRecipients.length,
            })}
          </h3>
        </div>
        <Button variant="ghost" onClick={() => onClose()} aria-label={t('common.cancel')}>
          <X weight="bold" />
        </Button>
      </header>

      <RecipientBuilder
        slug={slug}
        token={token}
        recipients={recipients}
        setRecipients={setRecipients}
        excludeLeaders={excludeLeaders}
        setExcludeLeaders={setExcludeLeaders}
        visibleCount={visibleRecipients.length}
      />

      <section className="flex flex-col gap-3">
        <h4 className="text-sm font-semibold">
          {t('billing.invoices.create.linesHeading', {
            defaultValue: 'Line items (applied to every invoice)',
          })}
        </h4>
        <LineEditor lines={lines} setLines={setLines} t={t} />
        <Button
          variant="outline"
          className="w-full"
          onClick={() =>
            setLines([
              ...lines,
              {
                ord: lines.length,
                description: '',
                quantity_milli: 1000,
                unit_amount_minor: 0,
                vat_rate_bp: 0,
              },
            ])
          }
        >
          <Plus weight="bold" />
          {t('billing.invoices.addLine', { defaultValue: 'Add line' })}
        </Button>
        <dl className="grid grid-cols-2 gap-y-1 rounded-2xl bg-muted/40 p-3 text-sm">
          <dt>{t('billing.invoices.subtotal', { defaultValue: 'Subtotal' })}</dt>
          <dd className="text-right">{formatMoney(subtotal, currencyForDisplay)}</dd>
          <dt>{t('billing.invoices.vat', { defaultValue: 'VAT' })}</dt>
          <dd className="text-right">{formatMoney(vat, currencyForDisplay)}</dd>
          <dt className="font-semibold">
            {t('billing.invoices.create.totalPer', { defaultValue: 'Total per invoice' })}
          </dt>
          <dd className="text-right font-semibold">{formatMoney(total, currencyForDisplay)}</dd>
          <dt className="text-muted-foreground">
            {t('billing.invoices.create.grandTotal', {
              defaultValue: 'Grand total ({{n}} × invoice)',
              n: visibleRecipients.length,
            })}
          </dt>
          <dd className="text-right tabular-nums">
            {formatMoney(total * visibleRecipients.length, currencyForDisplay)}
          </dd>
        </dl>

        {/* Föreningsstyrd payout estimate: what the club nets per invoice after
            Stripe's fee + our platform fee. Estimated — the real Stripe fee is
            only known after payment — so it's labelled accordingly. */}
        {total > 0 &&
          (() => {
            const payout = estimatePayout(total);
            return (
              <dl className="grid grid-cols-2 gap-y-1 rounded-2xl border border-dashed border-border p-3 text-sm">
                <dt className="col-span-2 mb-0.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('billing.invoices.create.payout.heading', {
                    defaultValue: 'Estimated payout per invoice',
                  })}
                </dt>
                <dt className="text-muted-foreground">
                  {t('billing.invoices.create.payout.stripeFee', { defaultValue: 'Stripe fee (est.)' })}
                </dt>
                <dd className="text-right tabular-nums text-muted-foreground">
                  −{formatMoney(payout.stripeFeeMinor, currencyForDisplay)}
                </dd>
                <dt className="text-muted-foreground">
                  {t('billing.invoices.create.payout.platformFee', { defaultValue: 'SportAdmin fee' })}
                </dt>
                <dd className="text-right tabular-nums text-muted-foreground">
                  −{formatMoney(payout.platformFeeMinor, currencyForDisplay)}
                </dd>
                <dt className="font-semibold">
                  {t('billing.invoices.create.payout.net', { defaultValue: 'You receive (est.)' })}
                </dt>
                <dd className="text-right font-semibold tabular-nums">
                  {formatMoney(payout.netMinor, currencyForDisplay)}
                </dd>
                {visibleRecipients.length > 1 && (
                  <>
                    <dt className="text-muted-foreground">
                      {t('billing.invoices.create.payout.grandNet', {
                        defaultValue: 'Total payout ({{n}} invoices)',
                        n: visibleRecipients.length,
                      })}
                    </dt>
                    <dd className="text-right tabular-nums text-muted-foreground">
                      {formatMoney(payout.netMinor * visibleRecipients.length, currencyForDisplay)}
                    </dd>
                  </>
                )}
              </dl>
            );
          })()}
      </section>

      <label className="flex flex-col gap-1.5 text-sm font-medium">
        <span>{t('billing.invoices.create.notes', { defaultValue: 'Notes (optional)' })}</span>
        <Input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={500}
          placeholder={
            t('billing.invoices.create.notesPlaceholder', {
              defaultValue: 'Visible on the invoice.',
            }) ?? ''
          }
        />
      </label>

      {errorCode && (
        <p role="alert" className="text-sm text-destructive">
          {t(`billing.invoices.create.error.${errorCode}`, {
            defaultValue: t('billing.invoices.create.error.unknown', {
              defaultValue: 'Could not create. Try again.',
            }),
          })}
        </p>
      )}

      {!stripeReady && (
        <p className="mt-2 text-right text-xs text-muted-foreground">
          {t('billing.invoices.create.stripeRequired', {
            defaultValue: 'Connect Stripe to send invoices. You can still save a draft.',
          })}
        </p>
      )}
      <footer className="mt-auto flex flex-wrap items-center justify-end gap-2">
        <Button variant="outline" onClick={() => onClose()} disabled={submitMutation.isPending}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="outline"
          onClick={() => submitMutation.mutate(false)}
          disabled={!draftValid || submitMutation.isPending}
        >
          {t('billing.invoices.create.saveDraft', { defaultValue: 'Save as draft' })}
        </Button>
        <Button
          onClick={() => submitMutation.mutate(true)}
          disabled={!valid || submitMutation.isPending || !stripeReady}
          title={
            !stripeReady
              ? t('billing.invoices.create.stripeRequired', {
                  defaultValue: 'Connect Stripe to send invoices. You can still save a draft.',
                })
              : undefined
          }
        >
          <PaperPlaneTilt weight="bold" />
          {t('billing.invoices.create.issueNow', {
            defaultValue: 'Issue now ({{n}})',
            n: visibleRecipients.length,
          })}
        </Button>
      </footer>
    </aside>
  );
}

// RecipientBuilder is the heart of the new create flow: a chip list of
// selected recipients plus two add paths (members search and team picker)
// that can be used in any combination. Re-adding a member is a no-op
// (member_id dedup), so the user can safely click "Add team" multiple
// times or hop between picker modes without producing dupes.
function RecipientBuilder({
  slug,
  token,
  recipients,
  setRecipients,
  excludeLeaders,
  setExcludeLeaders,
  visibleCount,
}: {
  slug: string;
  token: string | null;
  recipients: CreateRecipient[];
  setRecipients: (next: CreateRecipient[]) => void;
  excludeLeaders: boolean;
  setExcludeLeaders: (v: boolean) => void;
  visibleCount: number;
}) {
  const { t } = useTranslation('common');
  const [memberSearch, setMemberSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(memberSearch), 200);
    return () => clearTimeout(h);
  }, [memberSearch]);

  const membersQuery = useQuery({
    queryKey: ['members', slug, debouncedSearch, 'invoice-create'],
    queryFn: () => listMembers(token, slug, { search: debouncedSearch, limit: 15 }),
    enabled: !!token && debouncedSearch.length > 0,
  });

  const teamsQuery = useQuery({
    queryKey: ['teams', slug, 'invoice-create'],
    queryFn: () => listTeams(token, slug),
    enabled: !!token,
  });

  const categoriesQuery = useQuery({
    queryKey: ['team-categories', slug, 'invoice-create'],
    queryFn: () => listTeamCategories(token, slug),
    enabled: !!token,
  });

  // A single search box drives one results dropdown spanning three result
  // kinds — members, teams, and team categories. Picking a member adds just
  // them; picking a team or category fans out to every member underneath
  // (dedup by member_id). comboOpen + comboRef drive the dropdown's
  // open/close; bulkBusy gates the async team/category fan-outs.
  const [comboOpen, setComboOpen] = useState(false);
  const comboRef = useRef<HTMLDivElement>(null);
  useClickOutside(comboRef, comboOpen, () => setComboOpen(false));
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);

  // Teams + categories filter client-side against the same query the member
  // search uses — the lists are small (tens of rows) so no extra round-trip.
  // An empty query shows all teams/categories so the box doubles as a browse
  // affordance; members only appear once there's something to search for.
  const teamMatches = useMemo(() => {
    const all = teamsQuery.data?.teams ?? [];
    const q = memberSearch.trim().toLowerCase();
    if (!q) return all;
    return all.filter((tm) => tm.name.toLowerCase().includes(q));
  }, [teamsQuery.data, memberSearch]);

  const categoryMatches = useMemo(() => {
    const all = categoriesQuery.data?.categories ?? [];
    const q = memberSearch.trim().toLowerCase();
    if (!q) return all;
    return all.filter((c) => c.name.toLowerCase().includes(q));
  }, [categoriesQuery.data, memberSearch]);

  // addMember dedupes by member_id so re-adds (via search OR team) are
  // safe. Returns the next list without mutating in place.
  function addMember(m: { id: string; first_name: string; last_name: string; email: string }, role?: 'athlete' | 'leader', teamID?: string) {
    if (recipients.some((r) => r.kind === 'member' && r.member_id === m.id)) return;
    setRecipients([
      ...recipients,
      {
        kind: 'member',
        member_id: m.id,
        first_name: m.first_name,
        last_name: m.last_name,
        email: m.email,
        ...(role && { role }),
        ...(teamID && { team_id: teamID }),
      },
    ]);
  }

  function addManual() {
    setRecipients([
      ...recipients,
      {
        kind: 'manual',
        tempID: `manual-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        customer_name: '',
        customer_email: '',
      },
    ]);
  }

  // buildAdditions turns one team's roster into dedup'd CreateRecipient
  // rows, skipping anyone already chosen. `seen` is mutated so a single
  // category fan-out across many teams never adds the same member twice.
  function buildAdditions(
    rows: Array<{
      member_id: string;
      first_name: string;
      last_name: string;
      email: string;
      role: 'athlete' | 'leader';
    }>,
    teamID: string,
    seen: Set<string>,
  ): CreateRecipient[] {
    const out: CreateRecipient[] = [];
    for (const m of rows) {
      if (seen.has(m.member_id)) continue;
      seen.add(m.member_id);
      out.push({
        kind: 'member',
        member_id: m.member_id,
        first_name: m.first_name,
        last_name: m.last_name,
        email: m.email,
        role: m.role,
        team_id: teamID,
      });
    }
    return out;
  }

  function chosenMemberIDs(): Set<string> {
    return new Set(
      recipients
        .filter((r) => r.kind === 'member')
        .map((r) => (r as Extract<CreateRecipient, { kind: 'member' }>).member_id),
    );
  }

  // addTeamAll fetches one team's roster and adds everyone (dedup). Fired
  // when the user picks a team from the unified search dropdown.
  async function addTeamAll(team: Team) {
    if (!token || bulkBusy) return;
    setBulkBusy(true);
    setBulkError(null);
    try {
      const res = await listTeamMembers(token, slug, team.id, { limit: 500 });
      const additions = buildAdditions(res.members, team.id, chosenMemberIDs());
      if (additions.length > 0) setRecipients([...recipients, ...additions]);
    } catch {
      setBulkError(
        t('billing.invoices.create.teamLoadFailed', {
          defaultValue: 'Could not load that team. Try again.',
        }) ?? null,
      );
    } finally {
      setBulkBusy(false);
    }
  }

  // addCategoryAll fans out: every team in the category → list its members →
  // merge into recipients (dedup by member_id). N parallel listTeamMembers
  // calls; a single failure surfaces an error string but does NOT roll back
  // additions that already landed (Promise.allSettled semantics) — the user
  // can retry after fixing whatever broke.
  async function addCategoryAll(categoryID: string) {
    if (!categoryID || !token || bulkBusy) return;
    const teams = (teamsQuery.data?.teams ?? []).filter((tm) => tm.category_id === categoryID);
    if (teams.length === 0) {
      setBulkError(
        t('billing.invoices.create.categoryNoTeams', {
          defaultValue: 'No teams in this category.',
        }) ?? null,
      );
      return;
    }
    setBulkBusy(true);
    setBulkError(null);
    try {
      const results = await Promise.allSettled(
        teams.map((tm) => listTeamMembers(token, slug, tm.id, { limit: 500 })),
      );
      const seen = chosenMemberIDs();
      const additions: CreateRecipient[] = [];
      results.forEach((res, idx) => {
        if (res.status !== 'fulfilled') return;
        const team = teams[idx];
        if (!team) return;
        additions.push(...buildAdditions(res.value.members, team.id, seen));
      });
      if (additions.length > 0) setRecipients([...recipients, ...additions]);
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) {
        setBulkError(
          t('billing.invoices.create.categoryPartialFailure', {
            defaultValue: 'Some teams could not be loaded ({{n}}). Try again.',
            n: failed,
          }) ?? null,
        );
      }
    } finally {
      setBulkBusy(false);
    }
  }

  function removeRecipient(idx: number) {
    setRecipients(recipients.filter((_, i) => i !== idx));
  }

  function updateManual(idx: number, patch: Partial<Extract<CreateRecipient, { kind: 'manual' }>>) {
    setRecipients(
      recipients.map((r, i) => (i === idx && r.kind === 'manual' ? { ...r, ...patch } : r)),
    );
  }

  // The member section only appears once there's a query (the server search
  // needs one); teams/categories show regardless. noComboResults guards the
  // "nothing matches" line so it never shows while members are still loading.
  const showMemberResults = debouncedSearch.length > 0;
  const memberResults = showMemberResults ? membersQuery.data?.members ?? [] : [];
  const noComboResults =
    teamMatches.length === 0 &&
    categoryMatches.length === 0 &&
    (!showMemberResults || (!membersQuery.isLoading && memberResults.length === 0));

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-semibold">
          {t('billing.invoices.create.recipients', {
            defaultValue: 'Recipients ({{n}})',
            n: visibleCount,
          })}
        </h4>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <Checkbox
            checked={excludeLeaders}
            onChange={(e) => setExcludeLeaders(e.target.checked)}
          />
          {t('billing.invoices.create.excludeLeaders', {
            defaultValue: 'Exclude leaders',
          })}
        </label>
      </div>

      {/* One search box, three result kinds. Members need a query; teams and
          categories also show on an empty box so it doubles as a browse menu.
          Picking a member adds them; picking a team/category fans out to its
          whole roster. */}
      <div ref={comboRef} className="relative">
        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t('billing.invoices.create.search', { defaultValue: 'Add recipients' })}
            {bulkBusy && (
              <span className="font-normal normal-case tracking-normal text-muted-foreground">
                {t('billing.invoices.create.bulkAdding', { defaultValue: 'Adding…' })}
              </span>
            )}
          </span>
          <div className="flex items-center gap-2">
            <SearchInput
              wrapperClassName="flex-1"
              value={memberSearch}
              onChange={(e) => {
                setMemberSearch(e.target.value);
                setComboOpen(true);
              }}
              onFocus={() => setComboOpen(true)}
              placeholder={
                t('billing.invoices.create.searchPlaceholder', {
                  defaultValue: 'Search members, teams or categories…',
                }) ?? ''
              }
              clearLabel={t('common.clearSearch')}
            />
            {/* Green plus adds a manual free-typed recipient row — the
                primary (lime) variant makes it the one loud affordance
                sitting next to the search field. */}
            <Button
              size="icon"
              onClick={addManual}
              aria-label={
                t('billing.invoices.create.addManual', {
                  defaultValue: 'Add manual recipient',
                }) ?? undefined
              }
            >
              <Plus weight="bold" />
            </Button>
          </div>
        </div>

        {comboOpen && (
          <div className="absolute left-0 right-0 z-20 mt-1 overflow-hidden rounded-xl border border-border bg-background shadow-lg">
            <div className="max-h-72 overflow-y-auto py-1">
              {showMemberResults && (
                <>
                  <ComboGroupLabel>
                    {t('billing.invoices.create.group.members', { defaultValue: 'Members' })}
                  </ComboGroupLabel>
                  {membersQuery.isLoading && (
                    <p className="px-3 py-2 text-sm text-muted-foreground">{t('common.loading')}</p>
                  )}
                  {!membersQuery.isLoading && memberResults.length === 0 && (
                    <p className="px-3 py-2 text-sm text-muted-foreground">
                      {t('billing.invoices.create.searchEmpty', { defaultValue: 'No members match.' })}
                    </p>
                  )}
                  {memberResults.map((m: Member) => {
                    const fullName = `${m.first_name} ${m.last_name}`.trim();
                    const already = recipients.some(
                      (r) => r.kind === 'member' && r.member_id === m.id,
                    );
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => {
                          addMember({
                            id: m.id,
                            first_name: m.first_name,
                            last_name: m.last_name,
                            email: m.email,
                          });
                          setMemberSearch('');
                          setComboOpen(false);
                        }}
                        disabled={already}
                        className={cn(
                          'flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors',
                          already ? 'opacity-50' : 'hover:bg-muted/50',
                        )}
                      >
                        <span className="flex flex-col">
                          <span className="text-sm font-medium">{fullName || '—'}</span>
                          <span className="text-xs text-muted-foreground">{m.email || '—'}</span>
                        </span>
                        {already && (
                          <span className="text-xs text-muted-foreground">
                            {t('billing.invoices.create.alreadyAdded', { defaultValue: 'Added' })}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </>
              )}

              {teamMatches.length > 0 && (
                <>
                  <ComboGroupLabel>
                    {t('billing.invoices.create.group.teams', { defaultValue: 'Teams' })}
                  </ComboGroupLabel>
                  {teamMatches.map((tm) => {
                    // Grey out a team once every one of its members is already a
                    // recipient — mirrors the per-member "Added" state. We count
                    // recipients tagged with this team_id (set when a member is
                    // added via a team/category) against the team's roster size.
                    const addedInTeam = recipients.filter(
                      (r) => r.kind === 'member' && r.team_id === tm.id,
                    ).length;
                    const teamFull = tm.member_count > 0 && addedInTeam >= tm.member_count;
                    return (
                      <button
                        key={tm.id}
                        type="button"
                        disabled={bulkBusy || teamFull}
                        onClick={() => {
                          void addTeamAll(tm);
                          setMemberSearch('');
                          setComboOpen(false);
                        }}
                        className={cn(
                          'flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors disabled:opacity-50',
                          teamFull ? 'opacity-50' : 'hover:bg-muted/50',
                        )}
                      >
                        <span className="flex items-center gap-2 text-sm font-medium">
                          <UsersThree weight="bold" className="h-4 w-4 text-muted-foreground" aria-hidden />
                          {tm.name}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {teamFull
                            ? t('billing.invoices.create.alreadyAdded', { defaultValue: 'Added' })
                            : t('billing.invoices.create.addWholeTeam', { defaultValue: 'Add whole team' })}
                        </span>
                      </button>
                    );
                  })}
                </>
              )}

              {categoryMatches.length > 0 && (
                <>
                  <ComboGroupLabel>
                    {t('billing.invoices.create.group.categories', { defaultValue: 'Team categories' })}
                  </ComboGroupLabel>
                  {categoryMatches.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      disabled={bulkBusy}
                      onClick={() => {
                        void addCategoryAll(c.id);
                        setMemberSearch('');
                        setComboOpen(false);
                      }}
                      className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors hover:bg-muted/50 disabled:opacity-50"
                    >
                      <span className="flex items-center gap-2 text-sm font-medium">
                        <Users weight="bold" className="h-4 w-4 text-muted-foreground" aria-hidden />
                        {c.name}
                        <span className="text-xs font-normal text-muted-foreground">
                          {t('billing.invoices.create.categoryTeamCount', {
                            defaultValue: '({{n}} teams)',
                            n: c.team_count,
                          })}
                        </span>
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {t('billing.invoices.create.addWholeCategory', { defaultValue: 'Add all members' })}
                      </span>
                    </button>
                  ))}
                </>
              )}

              {noComboResults && (
                <p className="px-3 py-3 text-center text-sm text-muted-foreground">
                  {t('billing.invoices.create.comboEmpty', {
                    defaultValue: 'No members, teams or categories match.',
                  })}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {bulkError && <p className="text-xs text-red-600">{bulkError}</p>}

      {recipients.length > 0 && (
        <RecipientTable
          recipients={recipients}
          teams={teamsQuery.data?.teams ?? []}
          excludeLeaders={excludeLeaders}
          onRemove={removeRecipient}
          onManualChange={updateManual}
          t={t}
        />
      )}
    </section>
  );
}

// ComboGroupLabel is the section heading inside the unified search dropdown
// (Members / Teams / Team categories). Mirrors SelectLabel's styling so the
// dropdown reads as a sibling of the branded Select menus.
function ComboGroupLabel({ children }: { children: ReactNode }) {
  return (
    <p className="bg-muted/30 px-3 py-1 font-heading text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
      {children}
    </p>
  );
}

// RecipientTable shows the resolved recipient list as rows below the add
// card. Each row reveals the source (member / team / manual), the role,
// and which team the member came from when added via team or category —
// so the admin can audit at a glance who is actually going to receive an
// invoice. Manual rows stay editable inline; member rows are read-only.
//
// Paged + searchable: a single invoice batch can have hundreds of
// recipients (whole-org category fan-out), so scrolling a flat list is
// hostile. Filtering happens client-side over the in-memory recipient
// array — no extra round-trips, dedup is already done up-front.
function RecipientTable({
  recipients,
  teams,
  excludeLeaders,
  onRemove,
  onManualChange,
  t,
}: {
  recipients: CreateRecipient[];
  teams: Team[];
  excludeLeaders: boolean;
  onRemove: (idx: number) => void;
  onManualChange: (idx: number, patch: Partial<Extract<CreateRecipient, { kind: 'manual' }>>) => void;
  t: TFunction<'common'>;
}) {
  const teamByID = useMemo(() => {
    const m = new Map<string, string>();
    teams.forEach((tm) => m.set(tm.id, tm.name));
    return m;
  }, [teams]);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Pair each recipient with its index in the parent array so onRemove /
  // onManualChange still receive the canonical position after filtering
  // and slicing.
  const indexed = useMemo(
    () => recipients.map((r, idx) => ({ r, idx })),
    [recipients],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return indexed;
    return indexed.filter(({ r }) => {
      if (r.kind === 'member') {
        const name = `${r.first_name} ${r.last_name}`.toLowerCase();
        const email = (r.email ?? '').toLowerCase();
        const team = (r.team_id ? teamByID.get(r.team_id) ?? '' : '').toLowerCase();
        return name.includes(q) || email.includes(q) || team.includes(q);
      }
      return (
        r.customer_name.toLowerCase().includes(q) ||
        r.customer_email.toLowerCase().includes(q)
      );
    });
  }, [indexed, search, teamByID]);

  // Re-anchor to page 1 when the filter narrows past the current page.
  // useEffect is overkill — useMemo with the side-effect would be wrong;
  // a tiny derived clamp keeps the rendered page valid every render.
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const pageRows = filtered.slice(pageStart, pageStart + pageSize);

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-border bg-background p-2">
      <div className="px-1 pt-1">
        <SearchInput
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder={
            t('billing.invoices.create.table.searchPlaceholder', {
              defaultValue: 'Filter recipients by name, email, or team…',
            }) ?? ''
          }
          clearLabel={t('common.clearSearch')}
        />
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              {t('billing.invoices.create.table.name', { defaultValue: 'Name' })}
            </TableHead>
            <TableHead>
              {t('billing.invoices.create.table.email', { defaultValue: 'Email' })}
            </TableHead>
            <TableHead>
              {t('billing.invoices.create.table.team', { defaultValue: 'Team' })}
            </TableHead>
            <TableHead>
              {t('billing.invoices.create.table.role', { defaultValue: 'Role' })}
            </TableHead>
            <TableHead aria-label={t('common.actions', { defaultValue: 'Actions' }) ?? undefined} />
          </TableRow>
        </TableHeader>
        <TableBody>
          {pageRows.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="py-6 text-center text-sm text-muted-foreground">
                {t('billing.invoices.create.table.empty', {
                  defaultValue: 'No matching recipients.',
                })}
              </TableCell>
            </TableRow>
          )}
          {pageRows.map(({ r, idx }) => {
            const hidden = excludeLeaders && r.kind === 'member' && r.role === 'leader';
            if (r.kind === 'member') {
              const fullName = `${r.first_name} ${r.last_name}`.trim() || '—';
              const teamName = r.team_id ? teamByID.get(r.team_id) ?? '—' : '—';
              return (
                <TableRow key={r.member_id} className={hidden ? 'opacity-40' : undefined}>
                  <TableCell className="font-medium">{fullName}</TableCell>
                  <TableCell className="text-muted-foreground">{r.email || '—'}</TableCell>
                  <TableCell className="text-muted-foreground">{teamName}</TableCell>
                  <TableCell>
                    {r.role ? (
                      <Badge variant="secondary">
                        {t(`members.teams.role.${r.role}`, {
                          defaultValue: r.role.charAt(0).toUpperCase() + r.role.slice(1),
                        })}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                    {hidden && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        {t('billing.invoices.create.hidden', { defaultValue: 'hidden' })}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <button
                      type="button"
                      onClick={() => onRemove(idx)}
                      className="rounded-pill p-1 text-destructive hover:bg-destructive/10 hover:text-destructive"
                      aria-label={t('common.cancel')}
                    >
                      <X className="h-4 w-4" aria-hidden weight="bold" />
                    </button>
                  </TableCell>
                </TableRow>
              );
            }
            return (
              <TableRow key={r.tempID}>
                <TableCell>
                  <Input
                    value={r.customer_name}
                    onChange={(e) => onManualChange(idx, { customer_name: e.target.value })}
                    placeholder={
                      t('billing.invoices.create.manualNamePlaceholder', { defaultValue: 'Customer name' }) ?? ''
                    }
                    className="h-8 text-sm"
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="email"
                    value={r.customer_email}
                    onChange={(e) => onManualChange(idx, { customer_email: e.target.value })}
                    placeholder={
                      t('billing.invoices.create.manualEmailPlaceholder', { defaultValue: 'email (optional)' }) ?? ''
                    }
                    className="h-8 text-sm"
                  />
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {t('billing.invoices.create.table.manual', { defaultValue: 'Manual' })}
                </TableCell>
                <TableCell className="text-muted-foreground">—</TableCell>
                <TableCell className="text-right">
                  <button
                    type="button"
                    onClick={() => onRemove(idx)}
                    className="rounded-pill p-1 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    aria-label={t('common.cancel')}
                  >
                    <X className="h-4 w-4" aria-hidden weight="bold" />
                  </button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <TablePagination
        total={filtered.length}
        page={safePage}
        size={pageSize}
        pageSizes={[10, 25, 50, 100]}
        onPageChange={setPage}
        onSizeChange={(s) => {
          setPageSize(s);
          setPage(1);
        }}
      />
    </div>
  );
}

// ----------------- RecipientSection (used inside InvoiceEditor) ------------------

interface RecipientForm {
  member_id: string;
  invoice_type_id: string;
  customer_name: string;
  customer_email: string;
  customer_address_line1: string;
  customer_address_line2: string;
  customer_postal_code: string;
  customer_city: string;
  customer_country: string;
}

function emptyRecipient(): RecipientForm {
  return {
    member_id: '',
    invoice_type_id: '',
    customer_name: '',
    customer_email: '',
    customer_address_line1: '',
    customer_address_line2: '',
    customer_postal_code: '',
    customer_city: '',
    customer_country: '',
  };
}

function toRecipientForm(inv: Invoice): RecipientForm {
  return {
    member_id: inv.member_id ?? '',
    invoice_type_id: inv.invoice_type_id ?? '',
    customer_name: inv.customer_name ?? '',
    customer_email: inv.customer_email ?? '',
    customer_address_line1: inv.customer_address_line1 ?? '',
    customer_address_line2: inv.customer_address_line2 ?? '',
    customer_postal_code: inv.customer_postal_code ?? '',
    customer_city: inv.customer_city ?? '',
    customer_country: inv.customer_country ?? '',
  };
}

function recipientDirty(inv: Invoice, form: RecipientForm): boolean {
  const server = toRecipientForm(inv);
  return (
    server.member_id !== form.member_id ||
    server.invoice_type_id !== form.invoice_type_id ||
    server.customer_name !== form.customer_name ||
    server.customer_email !== form.customer_email ||
    server.customer_address_line1 !== form.customer_address_line1 ||
    server.customer_address_line2 !== form.customer_address_line2 ||
    server.customer_postal_code !== form.customer_postal_code ||
    server.customer_city !== form.customer_city ||
    server.customer_country !== form.customer_country
  );
}

function applyMemberToRecipient(form: RecipientForm, m: Member): RecipientForm {
  const fullName = `${m.first_name} ${m.last_name}`.trim();
  return {
    ...form,
    member_id: m.id,
    customer_name: fullName || form.customer_name,
    customer_email: m.email || form.customer_email,
    customer_address_line1: m.street_address || form.customer_address_line1,
    customer_postal_code: m.postal_code || form.customer_postal_code,
    customer_city: m.city || form.customer_city,
  };
}

function RecipientSection({
  slug,
  token,
  inv,
  recipient,
  setRecipient,
  onSave,
  saving,
}: {
  slug: string;
  token: string | null;
  inv: Invoice;
  recipient: RecipientForm;
  setRecipient: (r: RecipientForm) => void;
  onSave: () => void;
  saving: boolean;
}) {
  const { t } = useTranslation('common');
  const isDraft = inv.status === 'draft';
  const [pickerOpen, setPickerOpen] = useState(false);
  const dirty = useMemo(() => recipientDirty(inv, recipient), [inv, recipient]);

  const typesQuery = useQuery({
    queryKey: ['billing-invoice-types', slug, false],
    queryFn: () => listInvoiceTypes(token, slug, false),
    enabled: !!token && !!slug,
  });
  const invoiceTypes = typesQuery.data ?? [];

  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-border/40 p-4">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold">
          {t('billing.invoices.recipient.title', { defaultValue: 'Recipient' })}
        </h4>
        {isDraft && (
          <Button
            variant="outline"
            onClick={() => setPickerOpen((v) => !v)}
            aria-pressed={pickerOpen}
          >
            <MagnifyingGlass weight="bold" className="h-4 w-4" />
            {recipient.member_id
              ? t('billing.invoices.recipient.changeMember', { defaultValue: 'Change member' })
              : t('billing.invoices.recipient.pickMember', { defaultValue: 'Pick from members' })}
          </Button>
        )}
      </div>

      {pickerOpen && isDraft && (
        <InlineMemberPicker
          slug={slug}
          token={token}
          pickedID={recipient.member_id || null}
          onPick={(m) => {
            setRecipient(applyMemberToRecipient(recipient, m));
            setPickerOpen(false);
          }}
          onClear={() => {
            setRecipient({ ...recipient, member_id: '' });
          }}
        />
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5 text-sm font-medium sm:col-span-2">
          <span>{t('billing.invoices.recipient.invoiceType', { defaultValue: 'Invoice type' })}</span>
          <Select
            value={recipient.invoice_type_id || 'none'}
            disabled={!isDraft}
            onValueChange={(v) =>
              setRecipient({ ...recipient, invoice_type_id: v === 'none' ? '' : v })
            }
          >
            <SelectTrigger>
              <SelectValue
                placeholder={t('billing.invoices.recipient.invoiceTypeNone', {
                  defaultValue: 'No type',
                })}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">
                {t('billing.invoices.recipient.invoiceTypeNone', { defaultValue: 'No type' })}
              </SelectItem>
              {invoiceTypes.map((it) => (
                <SelectItem key={it.id} value={it.id}>
                  {it.name}
                  {it.is_membership_fee
                    ? ` · ${t('billing.invoiceTypes.membershipBadge', { defaultValue: 'Membership fee' })}`
                    : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <label className="flex flex-col gap-1.5 text-sm font-medium sm:col-span-2">
          <span>
            {t('billing.invoices.recipient.name', { defaultValue: 'Customer name' })}
            <span className="ml-1 text-destructive">{REQUIRED_MARK}</span>
          </span>
          <Input
            value={recipient.customer_name}
            disabled={!isDraft}
            onChange={(e) => setRecipient({ ...recipient, customer_name: e.target.value })}
            maxLength={200}
            required
          />
        </label>
        <label className="flex flex-col gap-1.5 text-sm font-medium sm:col-span-2">
          <span>{t('billing.invoices.recipient.email', { defaultValue: 'Email' })}</span>
          <Input
            type="email"
            value={recipient.customer_email}
            disabled={!isDraft}
            onChange={(e) => setRecipient({ ...recipient, customer_email: e.target.value })}
            maxLength={320}
          />
        </label>
        <label className="flex flex-col gap-1.5 text-sm font-medium sm:col-span-2">
          <span>{t('billing.invoices.recipient.address1', { defaultValue: 'Address line 1' })}</span>
          <Input
            value={recipient.customer_address_line1}
            disabled={!isDraft}
            onChange={(e) =>
              setRecipient({ ...recipient, customer_address_line1: e.target.value })
            }
            maxLength={200}
          />
        </label>
        <label className="flex flex-col gap-1.5 text-sm font-medium sm:col-span-2">
          <span>{t('billing.invoices.recipient.address2', { defaultValue: 'Address line 2' })}</span>
          <Input
            value={recipient.customer_address_line2}
            disabled={!isDraft}
            onChange={(e) =>
              setRecipient({ ...recipient, customer_address_line2: e.target.value })
            }
            maxLength={200}
          />
        </label>
        <label className="flex flex-col gap-1.5 text-sm font-medium">
          <span>{t('billing.invoices.recipient.postal', { defaultValue: 'Postal code' })}</span>
          <Input
            value={recipient.customer_postal_code}
            disabled={!isDraft}
            onChange={(e) =>
              setRecipient({ ...recipient, customer_postal_code: e.target.value })
            }
            maxLength={20}
          />
        </label>
        <label className="flex flex-col gap-1.5 text-sm font-medium">
          <span>{t('billing.invoices.recipient.city', { defaultValue: 'City' })}</span>
          <Input
            value={recipient.customer_city}
            disabled={!isDraft}
            onChange={(e) => setRecipient({ ...recipient, customer_city: e.target.value })}
            maxLength={120}
          />
        </label>
        <label className="flex flex-col gap-1.5 text-sm font-medium sm:col-span-2">
          <span>
            {t('billing.invoices.recipient.country', { defaultValue: 'Country (ISO code)' })}
          </span>
          <Input
            value={recipient.customer_country}
            disabled={!isDraft}
            onChange={(e) => setRecipient({ ...recipient, customer_country: e.target.value })}
            maxLength={2}
            placeholder="SE"
          />
        </label>
      </div>

      {isDraft && (
        <div className="flex items-center justify-end gap-2">
          {dirty && (
            <span className="text-xs text-muted-foreground">
              {t('billing.invoices.recipient.unsaved', { defaultValue: 'Unsaved changes' })}
            </span>
          )}
          <Button onClick={onSave} disabled={saving || !dirty || !recipient.customer_name.trim()}>
            {saving
              ? t('billing.invoices.recipient.saving', { defaultValue: 'Saving…' })
              : t('billing.invoices.recipient.save', { defaultValue: 'Save recipient' })}
          </Button>
        </div>
      )}
    </section>
  );
}

function InlineMemberPicker({
  slug,
  token,
  pickedID,
  onPick,
  onClear,
}: {
  slug: string;
  token: string | null;
  pickedID: string | null;
  onPick: (m: Member) => void;
  onClear: () => void;
}) {
  const { t } = useTranslation('common');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const h = setTimeout(() => setDebounced(search), 200);
    return () => clearTimeout(h);
  }, [search]);

  const membersQuery = useQuery({
    queryKey: ['members', slug, debounced, 'invoice-recipient'],
    queryFn: () => listMembers(token, slug, { search: debounced, limit: 25 }),
    enabled: !!token && !!slug,
  });
  const members = membersQuery.data?.members ?? [];

  return (
    <div className="flex flex-col gap-2 rounded-xl bg-muted/30 p-3">
      <div className="flex items-center gap-2">
        <SearchInput
          wrapperClassName="flex-1"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={
            t('billing.invoices.recipient.searchPlaceholder', {
              defaultValue: 'Search members by name or email…',
            }) ?? ''
          }
          clearLabel={t('common.clearSearch')}
          autoFocus
        />
        {pickedID && (
          <Button variant="outline" onClick={onClear}>
            {t('billing.invoices.recipient.detach', { defaultValue: 'Detach member' })}
          </Button>
        )}
      </div>
      <div className="max-h-56 overflow-y-auto rounded-xl border border-border bg-background">
        {membersQuery.isLoading && (
          <p className="px-3 py-2 text-sm text-muted-foreground">{t('common.loading')}</p>
        )}
        {!membersQuery.isLoading && members.length === 0 && (
          <p className="px-3 py-2 text-sm text-muted-foreground">
            {t('billing.invoices.recipient.empty', { defaultValue: 'No matching members.' })}
          </p>
        )}
        {members.map((m) => {
          const fullName = `${m.first_name} ${m.last_name}`.trim();
          const picked = pickedID === m.id;
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => onPick(m)}
              aria-pressed={picked}
              className={cn(
                'flex w-full items-center justify-between gap-3 border-b border-border px-3 py-2 text-left last:border-b-0 transition-colors',
                picked ? 'bg-primary/10' : 'hover:bg-muted/50',
              )}
            >
              <span className="flex flex-col">
                <span className="text-sm font-medium text-foreground">
                  {fullName || t('common.unnamed', { defaultValue: '—' })}
                </span>
                <span className="text-xs text-muted-foreground">
                  {m.email || m.phone || '—'}
                </span>
              </span>
              {picked && (
                <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                  {t('billing.invoices.recipient.picked', { defaultValue: 'Linked' })}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ----------------- helpers ------------------

function updateLine(
  set: (next: InvoiceLine[]) => void,
  lines: InvoiceLine[],
  idx: number,
  patch: Partial<InvoiceLine>,
) {
  set(lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
}

function lineTotal(l: InvoiceLine): number {
  return Math.floor((l.quantity_milli * l.unit_amount_minor) / 1000);
}
function lineVAT(l: InvoiceLine): number {
  return Math.floor((lineTotal(l) * l.vat_rate_bp) / 10000);
}

function formatMoney(minor: number, currency: string): string {
  if (!currency) return (minor / 100).toFixed(2);
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(minor / 100);
  } catch {
    return `${(minor / 100).toFixed(2)} ${currency}`;
  }
}

// CreateFamilyInvoicePanel (D4) — pick a guardian, load their dependents, enter
// each child's fee, see a live preview of the discounted lines, and create one
// combined draft invoice. The discount math is computed server-side (the engine
// in billing.family); this panel only sends the guardian + per-child base fees.
function CreateFamilyInvoicePanel({
  slug,
  token,
  currency,
  onClose,
}: {
  slug: string;
  token: string | null;
  currency: string;
  onClose: (createdID?: string) => void;
}) {
  const { t } = useTranslation('common');
  const toast = useToast();
  const panelRef = useRef<HTMLDivElement>(null);
  useClickOutside(panelRef, true, () => onClose());

  const [guardian, setGuardian] = useState<Member | null>(null);
  const [invoiceTypeId, setInvoiceTypeId] = useState<string>('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [fees, setFees] = useState<Record<string, string>>({});

  const typesQuery = useQuery({
    queryKey: ['billing-invoice-types', slug, false],
    queryFn: () => listInvoiceTypes(token, slug, false),
    enabled: !!token && !!slug,
  });
  // Default the type to the membership-fee type once loaded.
  useEffect(() => {
    if (!invoiceTypeId && typesQuery.data) {
      const membership = typesQuery.data.find((x) => x.is_membership_fee);
      if (membership) setInvoiceTypeId(membership.id);
    }
  }, [typesQuery.data, invoiceTypeId]);

  const dependentsQuery = useQuery({
    queryKey: ['member-dependents', slug, guardian?.id],
    queryFn: () => listDependents(token, slug, guardian!.id),
    enabled: !!token && !!slug && !!guardian,
  });
  const dependents: Dependent[] = useMemo(
    () => dependentsQuery.data?.dependents ?? [],
    [dependentsQuery.data],
  );

  // When dependents load, select them all by default.
  useEffect(() => {
    if (dependents.length > 0 && selected.size === 0) {
      setSelected(new Set(dependents.map((d) => d.member_id)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependentsQuery.data]);

  const members = useMemo(
    () =>
      dependents
        .filter((d) => selected.has(d.member_id))
        .map((d) => ({
          member_id: d.member_id,
          base_fee_minor: Math.round((Number.parseFloat(fees[d.member_id] ?? '') || 0) * 100),
        }))
        .filter((m) => m.base_fee_minor > 0),
    [dependents, selected, fees],
  );

  const canPreview = !!guardian && members.length > 0;

  const buildInput = (): CreateFamilyInvoiceInput => {
    const input: CreateFamilyInvoiceInput = { guardian_member_id: guardian!.id, members };
    if (invoiceTypeId) input.invoice_type_id = invoiceTypeId;
    return input;
  };

  const previewQuery = useQuery({
    queryKey: ['family-preview', slug, guardian?.id, invoiceTypeId, JSON.stringify(members)],
    queryFn: () => previewFamilyInvoice(token, slug, buildInput()),
    enabled: !!token && canPreview,
  });

  const createMutation = useMutation({
    mutationFn: () => createFamilyInvoice(token, slug, buildInput()),
    onSuccess: (res) => {
      toast.success(t('billing.family.created', { defaultValue: 'Family invoice drafted.' }));
      onClose(res.id);
    },
    onError: () => toast.error(t('billing.family.createFailed', { defaultValue: "Couldn't create the family invoice." })),
  });

  return (
    <aside
      ref={panelRef}
      className="fixed inset-y-0 right-0 z-30 flex w-full max-w-2xl flex-col gap-4 overflow-y-auto border-l border-border/60 bg-background p-6 shadow-2xl"
    >
      <header className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-semibold">
          {t('billing.family.createTitle', { defaultValue: 'New family invoice' })}
        </h3>
        <Button variant="ghost" onClick={() => onClose()} aria-label={t('common.cancel')}>
          <X weight="bold" />
        </Button>
      </header>

      {/* Guardian picker. */}
      <section className="flex flex-col gap-2 rounded-2xl border border-border/40 p-4">
        <h4 className="text-sm font-semibold">
          {t('billing.family.guardian', { defaultValue: 'Guardian (payer)' })}
        </h4>
        {guardian ? (
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm">{`${guardian.first_name} ${guardian.last_name}`.trim()}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setGuardian(null);
                setSelected(new Set());
                setFees({});
              }}
            >
              {t('billing.family.changeGuardian', { defaultValue: 'Change' })}
            </Button>
          </div>
        ) : (
          <InlineMemberPicker
            slug={slug}
            token={token}
            pickedID={null}
            onPick={(m) => setGuardian(m)}
            onClear={() => setGuardian(null)}
          />
        )}
      </section>

      {guardian && (
        <>
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            <span>{t('billing.family.invoiceType', { defaultValue: 'Invoice type' })}</span>
            <Select value={invoiceTypeId || 'none'} onValueChange={(v) => setInvoiceTypeId(v === 'none' ? '' : v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('billing.family.noType', { defaultValue: 'No type' })}</SelectItem>
                {(typesQuery.data ?? []).map((it) => (
                  <SelectItem key={it.id} value={it.id}>
                    {it.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>

          <section className="flex flex-col gap-2 rounded-2xl border border-border/40 p-4">
            <h4 className="text-sm font-semibold">
              {t('billing.family.children', { defaultValue: 'Family members to bill' })}
            </h4>
            {dependentsQuery.isPending ? (
              <p className="text-sm text-muted-foreground">{t('common.loading', { defaultValue: 'Loading…' })}</p>
            ) : dependents.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t('billing.family.noDependents', { defaultValue: 'This guardian has no linked members.' })}
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {dependents.map((d) => (
                  <div key={d.member_id} className="flex items-center gap-2">
                    <Checkbox
                      checked={selected.has(d.member_id)}
                      onChange={(e) => {
                        const next = new Set(selected);
                        if (e.target.checked) next.add(d.member_id);
                        else next.delete(d.member_id);
                        setSelected(next);
                      }}
                    />
                    <span className="flex-1 truncate text-sm">{`${d.first_name} ${d.last_name}`.trim()}</span>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      className="w-28"
                      disabled={!selected.has(d.member_id)}
                      placeholder={t('billing.family.feePlaceholder', { defaultValue: 'Fee' })}
                      value={fees[d.member_id] ?? ''}
                      onChange={(e) => setFees({ ...fees, [d.member_id]: e.target.value })}
                    />
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Live preview. */}
          {canPreview && (
            <section className="flex flex-col gap-2 rounded-2xl bg-muted/40 p-4">
              <h4 className="text-sm font-semibold">
                {t('billing.family.preview', { defaultValue: 'Preview' })}
              </h4>
              {previewQuery.isError ? (
                <p className="text-sm text-destructive">
                  {t('billing.family.previewError', {
                    defaultValue: 'Could not compute a preview. Check that family billing is enabled in settings.',
                  })}
                </p>
              ) : previewQuery.data ? (
                <>
                  <ul className="flex flex-col gap-1 text-sm">
                    {previewQuery.data.lines.map((l, i) => (
                      <li key={i} className="flex justify-between gap-2">
                        <span className="truncate text-muted-foreground">
                          {l.member_name || t(`billing.family.reason.${l.reason}`, { defaultValue: l.reason || '' })}
                        </span>
                        <span className={l.kind === 'discount' ? 'text-foreground/70' : ''}>
                          {formatMoney(l.amount_minor, currency)}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex justify-between border-t border-border pt-2 text-sm font-semibold">
                    <span>{t('billing.invoices.total', { defaultValue: 'Total' })}</span>
                    <span>{formatMoney(previewQuery.data.total_minor, currency)}</span>
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">{t('common.loading', { defaultValue: 'Loading…' })}</p>
              )}
            </section>
          )}

          <footer className="mt-auto flex items-center justify-end gap-2">
            <Button variant="outline" onClick={() => onClose()}>
              {t('common.cancel')}
            </Button>
            <Button onClick={() => createMutation.mutate()} disabled={!canPreview || createMutation.isPending}>
              {t('billing.family.createCta', { defaultValue: 'Create draft' })}
            </Button>
          </footer>
        </>
      )}
    </aside>
  );
}
